import { useEffect, useId, useMemo, useRef, useState } from "react";
import { LuCheck, LuSearch, LuX } from "react-icons/lu";

const defaultFilter = (options, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(q));
};

function listKeyHandler({ options, active, setActive, onPick, onClose }) {
  return (e) => {
    const last = options.length - 1;
    const moves = {
      ArrowDown: () => setActive((i) => Math.min(i + 1, last)),
      ArrowUp: () => setActive((i) => Math.max(i - 1, 0)),
      Home: () => setActive(0),
      End: () => setActive(last),
      Enter: () => options[active] && onPick(options[active].value),
      Escape: onClose,
      Tab: onClose,
    };
    const move = moves[e.key];
    if (!move) return;
    if (e.key !== "Tab") e.preventDefault();
    move();
  };
}

// The pop-up list behind every dropdown in the careers pages: a popover
// under its trigger on desktop, a bottom sheet on phones. Options are
// { value, label, hint?, icon?, group? }. With `searchable`, a search box
// filters them (pass `filter` for smarter matching than "label contains").
export default function Picker({
  title,
  options,
  value,
  onPick,
  onClose,
  searchable = false,
  searchPlaceholder = "Search",
  filter = defaultFilter,
  align = "left",
  emptyText = "Nothing matches",
}) {
  const [query, setQuery] = useState("");
  const shown = useMemo(() => filter(options, query), [filter, options, query]);
  const selectedIndex = shown.findIndex((o) => o.value === value);
  const [active, setActive] = useState(Math.max(selectedIndex, 0));
  const listRef = useRef(null);
  const listId = useId();
  const onKeyDown = listKeyHandler({ options: shown, active, setActive, onPick, onClose });

  useEffect(() => {
    if (query) setActive(0);
  }, [query]);

  useEffect(() => {
    if (!searchable) listRef.current?.focus();
  }, [searchable]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const optionId = (o) => `${listId}-${String(o.value).replace(/\W/g, "_")}`;
  const activeId = shown[active] ? optionId(shown[active]) : undefined;

  return (
    <>
      <div className="cr-picker-backdrop" onClick={onClose} aria-hidden="true" />
      <div className={`cr-picker cr-picker--${align}`} role="dialog" aria-label={title}>
        <div className="cr-picker-head">
          <span>{title}</span>
          <button type="button" className="cr-search-clear" onClick={onClose} aria-label="Close">
            <LuX />
          </button>
        </div>
        {searchable && (
          <div className="cr-picker-search">
            <LuSearch aria-hidden="true" />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={activeId}
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              autoComplete="off"
            />
          </div>
        )}
        <ul
          className="cr-picker-list"
          id={listId}
          role="listbox"
          ref={listRef}
          tabIndex={searchable ? -1 : 0}
          aria-label={title}
          aria-activedescendant={searchable ? undefined : activeId}
          onKeyDown={searchable ? undefined : onKeyDown}
        >
          {shown.map((o, i) => (
            <PickerOption
              key={String(o.value)}
              id={optionId(o)}
              option={o}
              index={i}
              selected={o.value === value}
              active={i === active}
              group={!query && o.group && o.group !== shown[i - 1]?.group ? o.group : null}
              onHover={() => setActive(i)}
              onPick={() => onPick(o.value)}
            />
          ))}
          {!shown.length && <li className="cr-picker-empty">{emptyText}</li>}
        </ul>
      </div>
    </>
  );
}

function PickerOption({ id, option, index, selected, active, group, onHover, onPick }) {
  return (
    <>
      {group && (
        <li className="cr-picker-group" role="presentation">
          {group}
        </li>
      )}
      <li
        id={id}
        role="option"
        aria-selected={selected}
        data-active={active}
        data-index={index}
        className="cr-picker-option"
        onMouseMove={onHover}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onPick}
      >
        {option.icon && (
          <span className="cr-picker-icon" aria-hidden="true">
            {option.icon}
          </span>
        )}
        <span className="cr-picker-name">{option.label}</span>
        {option.hint && <span className="cr-picker-code">{option.hint}</span>}
        {selected && <LuCheck className="cr-picker-check" aria-hidden="true" />}
      </li>
    </>
  );
}
